import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import { Badge, List, Drawer, Button, Typography, Space, Tabs, message } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import './NotificationCenter.css';
  import { useTranslation } from 'react-i18next';


const { TabPane } = Tabs;
const { Text, Title } = Typography;

/**
 * 通知去重处理函数
 */
const deduplicateNotifications = (notifications) => {
  if (!notifications || !Array.isArray(notifications)) {
    return [];
  }
  
  const seen = new Map();
  const deduplicated = [];
  
  for (const notification of notifications) {
    // 更宽松的去重策略：主要基于标题和内容的相似性，以及时间窗口
    const title = (notification.title || '').trim();
    const content = (notification.content || '').trim();
    
    // 提取时间，支持多种时间格式
    let timeKey = '';
    try {
      const time = notification.createTime || notification.timestamp || Date.now();
      const date = new Date(time);
      // 精确到5分钟窗口，允许更大的时间容差
      const minutes = Math.floor(date.getMinutes() / 5) * 5;
      timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    } catch (e) {
      timeKey = 'unknown';
    }
    
    // 如果是预约相关通知，使用更严格的去重
    if (title.includes('预约') || content.includes('预约')) {
      // 对于预约通知，只保留一个，不管具体内容差异
      const appointmentKey = `appointment_${timeKey}`;
      
      if (!seen.has(appointmentKey)) {
        seen.set(appointmentKey, notification);
        deduplicated.push(notification);
      } else {
        // 保留ID更大的（更新的）
        const existing = seen.get(appointmentKey);
        if (notification.id > existing.id) {
          const index = deduplicated.findIndex(n => n.id === existing.id);
          if (index !== -1) {
            deduplicated[index] = notification;
            seen.set(appointmentKey, notification);
          }
        }
      }
    } else {
      // 对于其他通知，使用原来的去重逻辑
      const dedupeKey = `${title}_${content}_${timeKey}`;
      
      if (!seen.has(dedupeKey)) {
        seen.set(dedupeKey, notification);
        deduplicated.push(notification);
      } else {
        const existing = seen.get(dedupeKey);
        if (notification.id > existing.id) {
          const index = deduplicated.findIndex(n => n.id === existing.id);
          if (index !== -1) {
            deduplicated[index] = notification;
            seen.set(dedupeKey, notification);
          }
        }
      }
    }
  }
  
  console.log('React端去重前通知数量:', notifications.length, '去重后数量:', deduplicated.length);
  return deduplicated;
};

/**
 * 通知中心组件
 * 展示用户的通知消息，支持标记已读
 */
const NotificationCenter = ({ userId }) => {
  const [visible, setVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [ws, setWs] = useState(null);

  
    const { t } = useTranslation();

  // 本地化通知内容
  const localizeNotification = (notification) => {
    const title = (notification.title || '').trim();
    const content = (notification.content || '').trim();
    
    // 用户端预约提醒：您有一个即将开始的预约...
    if (title === '预约提醒' || title === 'Appointment Reminder' || title === 'การแจ้งเตือนนัดหมาย') {
      if (content.includes('即将开始') || content.includes('upcoming') || content.includes('กำลังจะเริ่ม') || content.includes('请准时参加')) {
        return {
          title: t('notificationCenter.templates.appointmentReminderTitle'),
          content: t('notificationCenter.templates.appointmentReminderContent')
        };
      }
    }
    
    // 无法匹配，返回原内容
    return {
      title: notification.title,
      content: notification.content
    };
  };

  // 获取未读通知数量
  const fetchUnreadCount = () => {
    if (!userId) {
      console.warn('未提供用户ID，无法获取通知');
      return;
    }
    axios.get(`/notification/unread/${userId}`)
      .then(response => {
        if (response.data.code === 0) {
          // 对未读通知进行去重
          const deduplicatedNotifications = deduplicateNotifications(response.data.data);
          setUnreadCount(deduplicatedNotifications.length);
        }
      })
      .catch(error => {
        console.error('获取未读通知数量失败', error);
      });
  };

  // 获取通知列表
  const fetchNotifications = (type = null) => {
    if (!userId) {
      console.warn('未提供用户ID，无法获取通知');
      return;
    }
    setLoading(true);
    const params = { userId };
    if (type) {
      params.types = type;
    }

    axios.get(`/notification/list`, { params })
      .then(response => {
        if (response.data.code === 0) {
          // 对通知列表进行去重
          const deduplicatedNotifications = deduplicateNotifications(response.data.data);
          
          // 本地化每条通知
          const localizedNotifications = deduplicatedNotifications.map(notification => {
            const localized = localizeNotification(notification);
            return {
              ...notification,
              _originalTitle: notification.title,
              _originalContent: notification.content,
              title: localized.title,
              content: localized.content
            };
          });
          
          setNotifications(localizedNotifications);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('获取通知列表失败', error);
        setLoading(false);
      });
  };

  // 标记通知为已读
  const markAsRead = (notificationId) => {
    if (!userId) {
      console.warn('未提供用户ID，无法标记已读');
      return;
    }
    axios.post(`/notification/read/${notificationId}`, null, {
      params: { userId }
    })
      .then(response => {
        if (response.data.code === 0) {
          setNotifications(prev => 
            prev.map(n => 
              n.id === notificationId ? { ...n, isRead: 1 } : n
            )
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
          message.success(t('notificationCenter.messages.markReadSuccess'));
        } else {
          message.error(t('notificationCenter.messages.markReadFailed'));
        }
      })
      .catch(error => {
        console.error('标记已读失败', error);
        message.error(t('notificationCenter.messages.markReadFailed'));
      });
  };

  // 初始化WebSocket连接
  const initializeWebSocket = () => {
    if (!userId) {
      console.warn('未提供用户ID，无法建立WebSocket连接');
      return;
    }
    const apiBase = (process.env.REACT_APP_API_BASE_URL || '/springboot6hv37').replace(/\/$/, '')
    const origin = (() => {
      try { return new URL(apiBase, window.location.origin).origin } catch (e){ return window.location.origin }
    })()
    const wsProto = origin.startsWith('https') ? 'wss' : 'ws'
    const wsUrl = `${wsProto}://${new URL(origin).host}/springboot6hv37/ws/notification?userId=${userId}`
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log('WebSocket连接已建立');
      setWsConnected(true);
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('收到新通知', data);
      
      // 检查是否是重复通知
      const title = (data.title || '').trim();
      const content = (data.content || '').trim();
      
      // 生成时间窗口键
      let timeKey = '';
      try {
        const time = data.createTime || data.timestamp || Date.now();
        const date = new Date(time);
        const minutes = Math.floor(date.getMinutes() / 5) * 5;
        timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      } catch (e) {
        timeKey = 'unknown';
      }
      
      setNotifications(prev => {
        let isDuplicate = false;
        
        if (title.includes('预约') || content.includes('预约')) {
          // 对于预约通知，检查是否已存在相同时间窗口的预约通知
          isDuplicate = prev.some(notification => {
            const existingTitle = (notification.title || '').trim();
            const existingContent = (notification.content || '').trim();
            
            if (existingTitle.includes('预约') || existingContent.includes('预约')) {
              try {
                const existingTime = notification.createTime || notification.timestamp || Date.now();
                const existingDate = new Date(existingTime);
                const existingMinutes = Math.floor(existingDate.getMinutes() / 5) * 5;
                const existingTimeKey = `${existingDate.getFullYear()}-${String(existingDate.getMonth() + 1).padStart(2, '0')}-${String(existingDate.getDate()).padStart(2, '0')}_${String(existingDate.getHours()).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`;
                return existingTimeKey === timeKey;
              } catch (e) {
                return false;
              }
            }
            return false;
          });
        } else {
          // 对于其他通知，使用标题+内容+时间的去重
          isDuplicate = prev.some(notification => {
            const existingTitle = (notification.title || '').trim();
            const existingContent = (notification.content || '').trim();
            
            try {
              const existingTime = notification.createTime || notification.timestamp || Date.now();
              const existingDate = new Date(existingTime);
              const existingMinutes = Math.floor(existingDate.getMinutes() / 5) * 5;
              const existingTimeKey = `${existingDate.getFullYear()}-${String(existingDate.getMonth() + 1).padStart(2, '0')}-${String(existingDate.getDate()).padStart(2, '0')}_${String(existingDate.getHours()).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`;
              return existingTitle === title && existingContent === content && existingTimeKey === timeKey;
            } catch (e) {
              return existingTitle === title && existingContent === content;
            }
          });
        }
        
        if (!isDuplicate) {
          console.log('添加新通知:', data.title);
          
          // 本地化通知内容
          const localized = localizeNotification(data);
          const localizedData = {
            ...data,
            _originalTitle: data.title,
            _originalContent: data.content,
            title: localized.title,
            content: localized.content
          };
          
          // 更新未读数量
          setUnreadCount(count => count + 1);
          
          // 显示通知提醒
          message.info({
            content: localized.title,
            description: localized.content,
            duration: 5
          });
          
          return [localizedData, ...prev];
        } else {
          console.log('忽略重复通知:', data.title);
          return prev;
        }
      });
    };

    socket.onclose = () => {
      console.log('WebSocket连接已关闭');
      setWsConnected(false);
      // 尝试重新连接
      setTimeout(initializeWebSocket, 5000);
    };

    socket.onerror = (error) => {
      console.error('WebSocket错误', error);
      setWsConnected(false);
    };

    setWs(socket);
    
    return () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  };

  // 组件挂载时初始化
  useEffect(() => {
    if (userId) {
      fetchUnreadCount();
      initializeWebSocket();
    }
    
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [userId]);

  // 打开通知抽屉时获取通知
  const showDrawer = () => {
    setVisible(true);
    fetchNotifications();
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  // 根据通知类型渲染不同的图标
  const renderIcon = (type) => {
    switch (type) {
      case 'APPOINTMENT':
        return <span className="notification-icon appointment-icon">{t('notificationCenter.types.appointment')}</span>;
      case 'EXPIRATION':
        return <span className="notification-icon expiration-icon">{t('notificationCenter.types.expiration')}</span>;
      case 'PROMOTION':
        return <span className="notification-icon promotion-icon">{t('notificationCenter.types.promotion')}</span>;
      default:
        return <span className="notification-icon system-icon">{t('notificationCenter.types.system')}</span>;
    }
  };

  return (
    <>
      <Badge count={unreadCount} size="small" className='notification-badge'
>
        <Button 
          icon={<BellOutlined />} 
          shape="circle" 
          onClick={showDrawer}
          className={wsConnected ? 'notification-button connected' : 'notification-button'}
        />
      </Badge>
      
      <Drawer
        title={t('notificationCenter.title')}
        placement="right"
        onClose={closeDrawer}
        visible={visible}
        width={350}
      >
        <Tabs 
          defaultActiveKey="all"
          items={[
            {
              key: 'all',
              label: t('notificationCenter.tabs.all'),
              children: (
                <NotificationList 
                  notifications={notifications} 
                  loading={loading} 
                  markAsRead={markAsRead}
                  renderIcon={renderIcon}
                />
              )
            },
            {
              key: 'APPOINTMENT',
              label: t('notificationCenter.tabs.appointment'),
              children: (
                <NotificationList 
                  notifications={notifications.filter(n => n.businessType === 'APPOINTMENT')} 
                  loading={loading} 
                  markAsRead={markAsRead}
                  renderIcon={renderIcon}
                />
              )
            },
            {
              key: 'EXPIRATION',
              label: t('notificationCenter.tabs.expiration'),
              children: (
                <NotificationList 
                  notifications={notifications.filter(n => n.businessType === 'EXPIRATION')} 
                  loading={loading} 
                  markAsRead={markAsRead}
                  renderIcon={renderIcon}
                />
              )
            },
            {
              key: 'PROMOTION',
              label: t('notificationCenter.tabs.promotion'),
              children: (
                <NotificationList 
                  notifications={notifications.filter(n => n.businessType === 'PROMOTION')} 
                  loading={loading} 
                  markAsRead={markAsRead}
                  renderIcon={renderIcon}
                />
              )
            }
          ]}
        />
      </Drawer>
    </>
  );
};

// 通知列表组件
const NotificationList = ({ notifications, loading, markAsRead, renderIcon }) => (
  <List
    className="notification-list"
    loading={loading}
    itemLayout="horizontal"
    dataSource={notifications}
    locale={{ emptyText: (window.i18next && window.i18next.t) ? window.i18next.t('notificationCenter.empty') : '暂无通知' }}
    renderItem={item => (
      <List.Item
        className={item.isRead === 0 ? 'notification-item unread' : 'notification-item'}
        actions={[
          item.isRead === 0 ? (
            <Button 
              icon={<CheckOutlined />} 
              size="small" 
              type="text" 
              onClick={() => markAsRead(item.id)}
              title={ (window.i18next && window.i18next.t) ? window.i18next.t('notificationCenter.markRead') : '标记为已读' }
            />
          ) : null
        ]}
      >
        <List.Item.Meta
          avatar={renderIcon(item.businessType)}
          title={<Text strong={item.isRead === 0}>{item.title}</Text>}
          description={
            <Space direction="vertical" size={1}>
              <Text>{item.content}</Text>
              <Text type="secondary" className="notification-time">
                {new Date(item.createTime).toLocaleString()}
              </Text>
            </Space>
          }
        />
      </List.Item>
    )}
  />
);

export default NotificationCenter; 